<template>
  <div class="flex h-full min-h-0 flex-col">
    <!-- Header -->
    <div class="flex flex-none items-center justify-between px-1 pb-1">
      <h2 class="text-22-lh-36 font-black tracking-tight text-grey-800">Payment method</h2>
      <button
        type="button"
        class="-mr-1.5 flex h-11 w-11 items-center justify-center rounded-full text-grey-60"
        aria-label="Close"
        @click="$emit('close')"
      >
        <span class="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brown-50">
          <DsIcon name="close" size="sm" />
        </span>
      </button>
    </div>

    <!-- What is being paid for, and how much. Hoisted above every branch below:
         it used to live only in the linked-accounts view, so a member with no
         linked account — which is every book purchase, since those opt out of
         the bank rail — chose a payment method without the amount appearing
         anywhere in the app. The figure is the one the server just quoted, so
         this is the first place it can be stated truthfully. -->
    <div v-if="amount > 0" class="flex-none pt-1.5">
      <div class="inline-flex items-center gap-2 rounded-full bg-brown-75 px-3.5 py-2">
        <DsIcon name="bookmark" size="xs" class="text-brown-600" />
        <span class="text-body-small-md font-semibold text-brown-600">{{ summaryLabel }}</span>
      </div>
    </div>

    <!-- Loading skeleton -->
    <template v-if="loading">
      <div class="mt-4 space-y-3">
        <div
          v-for="n in 2"
          :key="'skeleton-' + n"
          class="flex w-full items-center gap-3 rounded-xl border border-brown-100 bg-white p-3.5"
        >
          <div class="h-[46px] w-[46px] flex-none animate-pulse rounded-[10px] bg-brown-50" />
          <div class="flex-1 space-y-2">
            <div class="h-3.5 w-2/3 animate-pulse rounded bg-brown-50" />
            <div class="h-3 w-1/3 animate-pulse rounded bg-brown-50" />
          </div>
          <div class="h-6 w-6 flex-none animate-pulse rounded-full bg-brown-50" />
        </div>
      </div>
    </template>

    <!-- Empty state — no linked accounts, so card / PayNow is the whole offer.
         There was a second "Link a bank account" tile here. Starting eGIRO setup
         is not something to do halfway through a payment: it closed the open
         transaction and sent the member to My Giving, which is where linking a
         bank belongs. A member who already has a mandate still sees it in the
         accounts view below. -->
    <div v-else-if="!linkedAccounts.length" class="flex flex-1 flex-col px-1 pb-4 pt-2">
      <!-- No heading here. The sticky header above already reads "Payment
           method" at the same size and weight, in every state, so this stacked a
           second 22px black heading directly under the first and asked the
           member the same question twice. The paragraph below keeps the giving
           voice where the rail allows it. -->
      <!-- No blurb either. It restated the tile directly beneath it ("Give
           instantly with card or PayNow" over a tile reading "Card or PayNow"),
           and with one option left there is nothing to explain or choose
           between. The chip above says what is being paid for and how much. -->
      <div class="mt-4 flex flex-col gap-2.5">
        <!-- Card / PayNow via HitPay hosted checkout (primary — surfaced first) -->
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-xl border-[1.5px] border-brown-800 bg-brown-50 p-3.5 text-left transition-colors active:opacity-80"
          aria-label="Pay with card or PayNow"
          data-testid="payment-tile-card"
          @click="$emit('checkout')"
        >
          <span
            class="flex h-[46px] w-[46px] flex-none items-center justify-center rounded-[10px]"
            style="background-color: #635bff"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="2" y="5" width="20" height="14" rx="2.5" />
              <path d="M2 10h20" />
            </svg>
          </span>
          <span class="min-w-0 flex-1">
            <span class="block text-body-md font-bold leading-tight text-grey-800">
              {{ cardRailLabel }}
            </span>
            <span class="mt-0.5 block text-body-small-md font-medium leading-tight text-grey-60">
              {{ cardRailSubtitle }}
            </span>
          </span>
          <DsIcon name="chevron-right" size="sm" class="flex-none text-grey-60" />
        </button>
      </div>

      <!-- Anchored to the foot of the sheet. This works because PaymentSheet
           now sizes its wrapper to the breakpoint's share of the viewport; while
           that box was the sheet's full travel, `mt-auto` put this line 284px
           below the screen and it vanished entirely at two thirds. -->
      <p class="mt-auto text-center text-body-small-md font-medium text-grey-60">
        <span class="mr-1 inline-block align-[-0.15em] text-grey-60">
          <DsIcon name="lock" size="xs" />
        </span>
        Your transaction is secured &amp; protected.
      </p>
    </div>

    <!-- Select view — accounts available -->
    <template v-else>
      <!-- Scrollable methods -->
      <div class="-mx-1 flex-1 overflow-y-auto overscroll-contain px-1 pb-2 pt-4">
        <!-- Linked accounts -->
        <div class="flex flex-col gap-2.5" role="radiogroup" aria-label="Linked accounts">
          <div class="text-body-small-md font-semibold text-grey-60">Linked accounts</div>
          <button
            v-for="account in linkedAccounts"
            :key="account.id"
            type="button"
            class="flex w-full items-center gap-3 rounded-xl border-[1.5px] p-3.5 text-left transition-colors"
            :class="
              selectedId === account.id
                ? 'border-brown-800 bg-brown-50'
                : 'border-brown-100 bg-white'
            "
            role="radio"
            :aria-checked="selectedId === account.id"
            :aria-label="`${account.bankName} Account ${account.lastFour}`"
            @click="selectAccount(account)"
          >
            <img
              v-if="hasIcon(account)"
              :src="account.iconPath"
              :alt="account.bankName"
              class="h-[46px] w-[46px] flex-none rounded-[10px] object-contain"
              @error="markIconFailed(account)"
            />
            <span
              v-else
              class="flex h-[46px] w-[46px] flex-none items-center justify-center rounded-[10px] bg-brown-800 text-caption-md font-extrabold text-white"
            >
              {{ bankInitials(account) }}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-body-md font-bold text-grey-800">
                {{ account.bankName }}
              </span>
              <span class="mt-0.5 block text-body-small-md font-medium text-grey-60">
                •••• {{ account.lastFour }}
              </span>
            </span>
            <span
              class="flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 transition-colors"
              :class="
                selectedId === account.id ? 'border-brown-800 bg-brown-800' : 'border-brown-100'
              "
            >
              <DsIcon v-if="selectedId === account.id" name="check" size="xs" class="text-white" />
            </span>
          </button>
        </div>

        <!-- Card / PayNow (HitPay) -->
        <div class="mt-4 flex flex-col gap-2.5">
          <div class="text-body-small-md font-semibold text-grey-60">{{ cardRailLabel }}</div>
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-xl border-[1.5px] border-brown-100 bg-white p-3.5 text-left transition-colors active:bg-brown-50"
            aria-label="Pay with card or PayNow"
            data-testid="payment-tile-card"
            @click="$emit('checkout')"
          >
            <span
              class="flex h-[46px] w-[46px] flex-none items-center justify-center rounded-[10px]"
              style="background-color: #635bff"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect x="2" y="5" width="20" height="14" rx="2.5" />
                <path d="M2 10h20" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-body-md font-bold leading-tight text-grey-800">
                {{ cardRailLabel }}
              </span>
              <span class="mt-0.5 block text-body-small-md font-medium leading-tight text-grey-60">
                {{ cardRailSubtitle }}
              </span>
            </span>
            <DsIcon name="chevron-right" size="sm" class="flex-none text-grey-60" />
          </button>
        </div>
      </div>

      <!-- Footer: slide to pay + security note -->
      <div class="flex-none pt-2.5">
        <SlideToGive
          ref="slider"
          :label="`Slide to pay ${payAmount}`"
          :disabled="!selectedId"
          @confirm="handleSlideConfirm"
        />
        <p class="mt-3.5 text-center text-body-small-md font-medium text-grey-60">
          <span class="mr-1 inline-block align-[-0.15em] text-grey-60">
            <DsIcon name="lock" size="xs" />
          </span>
          Your transaction is secured &amp; protected.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { Button as DsButton, Icon as DsIcon } from '@team-hogc/hogc-one-design-system';
import { CARD_RAIL_LABEL, CARD_RAIL_SUBTITLE } from '@/constants/Payment/payment-methods';
import SlideToGive from './SlideToGive.vue';

export default {
  name: 'PaymentMethodStep',

  components: {
    DsButton,
    DsIcon,
    SlideToGive,
  },

  props: {
    linkedAccounts: {
      type: Array,
      default: () => [],
    },
    // Whether the bank (eGIRO) rail is offered at all. Purchases disable it
    // until a FAST transfer settles against the payment ledger (epic
    // 86eynr4tx), and since purchases are all this sheet ever sees, it is off
    // everywhere today. It gates the accounts list only — no copy branches on
    // it any more.
    bankRailAllowed: {
      type: Boolean,
      default: true,
    },
    preselectedId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    amount: {
      type: Number,
      default: 0,
    },
    category: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
  },

  emits: ['select', 'confirm', 'checkout', 'close'],

  data() {
    return {
      selectedId: null,
      failedIconIds: [],
    };
  },

  computed: {
    // Both tiles read the same two strings. They were written out at each tile,
    // which is how one control ended up with two different subtitles ("Pay
    // securely…" in the empty view, "Tap to pay securely…" in the other) and
    // how the label stored on the ledger drifted away from either of them.
    cardRailLabel() {
      return CARD_RAIL_LABEL;
    },

    cardRailSubtitle() {
      return CARD_RAIL_SUBTITLE;
    },

    payAmount() {
      return `$${this.formatAmount(this.amount)}`;
    },

    summaryLabel() {
      // Always "Payment". Nothing reaches this sheet but purchases — the giving
      // flow has its own screens under /my-giving and has never opened it — so
      // the old "Giving" fallback described a path that does not exist.
      const label = this.description || this.category || 'Payment';
      return `${label} · ${this.payAmount} SGD`;
    },
  },

  watch: {
    // When the account list arrives (may be async), honour the caller's
    // preselected account if it's present; otherwise default to the first.
    linkedAccounts: {
      immediate: true,
      handler(accounts) {
        if (this.selectedId || !accounts.length) return;
        const preferred = this.preselectedId
          ? accounts.find(a => a.id === this.preselectedId)
          : null;
        this.selectAccount(preferred || accounts[0]);
      },
    },
  },

  methods: {
    formatAmount(value) {
      return Number(value || 0).toLocaleString('en-SG', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    selectAccount(account) {
      this.selectedId = account.id;
      this.$emit('select', account);
    },

    handleSlideConfirm() {
      if (!this.selectedId) return;
      this.$emit('confirm');
    },

    hasIcon(account) {
      return Boolean(account.iconPath) && !this.failedIconIds.includes(account.id);
    },

    markIconFailed(account) {
      if (!this.failedIconIds.includes(account.id)) {
        this.failedIconIds = [...this.failedIconIds, account.id];
      }
    },

    bankInitials(account) {
      return (account.bankName || account.bankCode || '?').slice(0, 4).toUpperCase();
    },

    /** Reset the slider after a failed attempt. Called by the parent via ref. */
    resetSlider() {
      this.$refs.slider?.reset();
    },
  },
};
</script>
